import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import store from "@/plugins/store/store";
import { Roles } from "@/constants/Roles";
import { resolveDefaultAuthenticatedRoute } from "@/utils/authRedirect";
import {
  ROUTE_DASHBOARD,
  ROUTE_LOGIN,
  ROUTE_RESET_PASSWORD,
  ROUTE_SET_PASSWORD,
  ROUTE_VERIFY_EMAIL,
  ROUTE_USERS,
  ROUTE_USERS_CREATE,
  ROUTE_USERS_DETAILS,
  ROUTE_USERS_EDIT,
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_VERIFICATION,
  ROUTE_TRANSFER_METHODS,
  ROUTE_TRANSFER_METHODS_CREATE,
  ROUTE_TRANSFER_METHODS_EDIT,
  ROUTE_TRANSFER_METHODS_DETAILS,
  ROUTE_PROVIDER_ASSIGNMENTS,
  ROUTE_BALANCE_HISTORY,
  ROUTE_OPERATIONS_ADMIN,
  ROUTE_OPERATIONS_AVAILABLE,
  ROUTE_OPERATIONS_AVAILABLE_DEPOSITS,
  ROUTE_OPERATIONS_AVAILABLE_WITHDRAWALS,
  ROUTE_OPERATIONS_MINE,
  ROUTE_OPERATIONS_MINE_DEPOSITS,
  ROUTE_OPERATIONS_MINE_WITHDRAWALS,
  ROUTE_OPERATIONS_DETAIL,
  ROUTE_TRANSFERS_DETAIL,
} from "@/constants/RouteNames";

const getDefaultAuthenticatedRoute = () =>
  resolveDefaultAuthenticatedRoute(store.getters["auth/userRole"]);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/login",
    name: ROUTE_LOGIN,
    component: () => import("../views/Auth/UserLogin.vue"),
    meta: { title: "login" },
  },
  {
    path: "/reset-password",
    name: ROUTE_RESET_PASSWORD,
    component: () => import("../views/Auth/ResetPassword.vue"),
    meta: { title: "reset-password" },
  },
  {
    path: "/set-password",
    name: ROUTE_SET_PASSWORD,
    component: () => import("../views/Auth/SetPassword.vue"),
    meta: { title: "set-password" },
  },
  {
    path: "/verify-email",
    name: ROUTE_VERIFY_EMAIL,
    component: () => import("../views/Auth/VerifyEmail.vue"),
    meta: { title: "verify-email" },
  },
  {
    path: "/",
    component: () => import("../views/Base.vue"),
    meta: { title: "users", auth: true },
    redirect: () => getDefaultAuthenticatedRoute(),
    children: [
      {
        path: "dashboard",
        name: ROUTE_DASHBOARD,
        component: () => import("../views/Dashboard/Index.vue"),
        meta: { title: "dashboard", auth: true },
      },
      {
        path: "users",
        name: ROUTE_USERS,
        component: () => import("../views/Users/Index.vue"),
        meta: { title: "users", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "users/create",
        name: ROUTE_USERS_CREATE,
        component: () => import("../views/Users/Form.vue"),
        meta: { title: "users-create", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "users/:id",
        name: ROUTE_USERS_DETAILS,
        component: () => import("../views/Users/Details.vue"),
        meta: { title: "users-details", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "users/:id/edit",
        name: ROUTE_USERS_EDIT,
        component: () => import("../views/Users/Form.vue"),
        meta: { title: "users-edit", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "settings",
        name: ROUTE_SETTINGS,
        redirect: { name: ROUTE_SETTINGS_VERIFICATION },
        meta: { title: "settings", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "settings/verification",
        name: ROUTE_SETTINGS_VERIFICATION,
        component: () => import("../views/Settings/Verification.vue"),
        meta: { title: "settings-verification", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "transfer-methods",
        name: ROUTE_TRANSFER_METHODS,
        component: () => import("../views/TransferMethods/Index.vue"),
        meta: { title: "transfer-methods", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "transfer-methods/create",
        name: ROUTE_TRANSFER_METHODS_CREATE,
        component: () => import("../views/TransferMethods/Form.vue"),
        meta: { title: "transfer-methods-create", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "transfer-methods/:id",
        name: ROUTE_TRANSFER_METHODS_DETAILS,
        component: () => import("../views/TransferMethods/Details.vue"),
        meta: { title: "transfer-methods-details", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "transfer-methods/:id/edit",
        name: ROUTE_TRANSFER_METHODS_EDIT,
        component: () => import("../views/TransferMethods/Form.vue"),
        meta: { title: "transfer-methods-edit", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "provider-assignments",
        name: ROUTE_PROVIDER_ASSIGNMENTS,
        component: () => import("../views/ProviderAssignments/Index.vue"),
        meta: { title: "provider-assignments", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "balance-history",
        name: ROUTE_BALANCE_HISTORY,
        component: () => import("../views/BalanceHistory/Index.vue"),
        meta: { title: "balance-history", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "operations",
        name: ROUTE_OPERATIONS_ADMIN,
        component: () => import("../views/Operations/Admin.vue"),
        meta: { title: "operations", auth: true, roles: [Roles.Admin] },
      },
      {
        path: "operations/available",
        name: ROUTE_OPERATIONS_AVAILABLE,
        redirect: { name: ROUTE_OPERATIONS_AVAILABLE_DEPOSITS },
        meta: { auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/available/deposits",
        name: ROUTE_OPERATIONS_AVAILABLE_DEPOSITS,
        component: () => import("../views/Operations/Available.vue"),
        props: { operationType: "Deposit" },
        meta: { title: "available-operations", auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/available/withdrawals",
        name: ROUTE_OPERATIONS_AVAILABLE_WITHDRAWALS,
        component: () => import("../views/Operations/Available.vue"),
        props: { operationType: "Withdrawal" },
        meta: { title: "available-operations", auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/mine",
        name: ROUTE_OPERATIONS_MINE,
        redirect: { name: ROUTE_OPERATIONS_MINE_DEPOSITS },
        meta: { auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/mine/deposits",
        name: ROUTE_OPERATIONS_MINE_DEPOSITS,
        component: () => import("../views/Operations/Mine.vue"),
        props: { operationType: "Deposit" },
        meta: { title: "my-operations", auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/mine/withdrawals",
        name: ROUTE_OPERATIONS_MINE_WITHDRAWALS,
        component: () => import("../views/Operations/Mine.vue"),
        props: { operationType: "Withdrawal" },
        meta: { title: "my-operations", auth: true, roles: [Roles.Provider] },
      },
      {
        path: "operations/:id",
        name: ROUTE_OPERATIONS_DETAIL,
        component: () => import("../views/Operations/Detail.vue"),
        meta: { title: "operation-detail", auth: true, roles: [Roles.Provider, Roles.Admin] },
      },
      {
        path: "transfers/:id",
        name: ROUTE_TRANSFERS_DETAIL,
        component: () => import("../views/Transfers/Detail.vue"),
        meta: { title: "transfer-detail", auth: true, roles: [Roles.Admin] },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, _from, next) => {
  const isAuthenticated = !!store.getters["auth/isAuthenticated"];
  const userRole = store.getters["auth/userRole"];

  if (to.matched.some((record) => record.meta.auth)) {
    if (!isAuthenticated) {
      return next({ path: "/login", query: { redirect: to.fullPath } });
    }

    const hasRoleRestriction = to.matched.some((record) => Array.isArray(record.meta.roles));
    if (hasRoleRestriction) {
      const allowed = to.matched.every((record) => {
        const roles = record.meta.roles as Roles[] | undefined;
        return !roles || roles.includes(userRole);
      });

      if (!allowed) {
        return next(getDefaultAuthenticatedRoute());
      }
    }

    return next();
  }

  if (to.name === ROUTE_LOGIN && isAuthenticated) {
    return next(getDefaultAuthenticatedRoute());
  }

  if (!isAuthenticated && to.path === "/") {
    return next({ path: "/login" });
  }

  return next();
});

router.afterEach((to) => {
  const routeTitle = String(to.meta.title || to.name || "Whitepay");
  document.title = `${routeTitle} | Whitepay`;
});

export default router;
